import { useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import { selectUser } from "../redux/slices/authSlice";
import { useUpdateProfileNameMutation } from "../redux/services/authApi";

// Lets a KYC screen fix the name captured at signup via a small edit affordance,
// since KYC verification fails if the name doesn't match the ID document exactly.
export const useKycNameEditor = () => {
  const user = useSelector(selectUser);
  const currentName = user?.name ?? "";

  const [isEditingName, setIsEditingName] = useState(false);
  const [draftName, setDraftName] = useState(currentName);
  const [updateProfileName, { isLoading: isSavingName }] = useUpdateProfileNameMutation();

  const startEditingName = () => {
    setDraftName(currentName);
    setIsEditingName(true);
  };

  const cancelEditingName = () => setIsEditingName(false);

  const saveName = async () => {
    const trimmed = draftName.trim().replace(/\s+/g, " ");
    if (!trimmed) {
      toast.error("Please enter your full name as it appears on your ID.");
      return;
    }

    if (trimmed === currentName.trim()) {
      setIsEditingName(false);
      return;
    }

    try {
      await updateProfileName({ name: trimmed }).unwrap();
      toast.success("Name updated successfully");
      setIsEditingName(false);
    } catch (error: any) {
      const fieldError = error?.data?.error?.message?.name?.[0];
      toast.error(
        fieldError ||
        error?.data?.error?.message ||
        error?.data?.message ||
        "Failed to update your name. Please try again."
      );
    }
  };

  return {
    currentName,
    isEditingName,
    draftName,
    setDraftName,
    startEditingName,
    cancelEditingName,
    saveName,
    isSavingName,
  };
};
